import type { RefOrGetter } from "ls/common/types";
import type { ComputedRef, MaybeRefOrGetter } from "vue";
import type { FuzzyResult, FuzzyTypes } from "./types";
import UFuzzy from "@leeoniya/ufuzzy";
import { computed, toValue } from "vue";
import { HIGHLIGHT_POST, HIGHLIGHT_PRE } from "@/common/axshare/constants";

export const ID_SEP = "|";

export function useFuzzySearch<T extends FuzzyTypes>(
  items: MaybeRefOrGetter<T[]>,
  getter: (item: T) => string,
  searchTerm: RefOrGetter<string>,
  { limit, hideIdHighlight = false }: { limit?: number; hideIdHighlight?: boolean } = {},
): ComputedRef<FuzzyResult<T>[]> {
  const uf = new UFuzzy({
    unicode: true,
    interSplit: "\\p{Punctuation}+",
    intraSplit: "\\p{Ll}\\p{Lu}",
    intraBound: "\\p{L}\\d|\\d\\p{L}|\\p{Ll}\\p{Lu}",
    intraChars: "[\\p{L}\\d']",
    intraContr: "'\\p{L}{1,2}\\b",
    intraMode: 1,
    intraIns: 1,
    intraSub: 1,
    intraTrn: 1,
    intraDel: 1,
  });

  const mark = (part: string, matched: boolean) =>
    matched ? `${HIGHLIGHT_PRE}${part}${HIGHLIGHT_POST}` : part;

  const haystack = computed(() => toValue(items).map(getter));

  const searchData = computed(() => {
    const needle = toValue(searchTerm).trim();
    if (!needle) return { idxs: [] as number[], info: null as UFuzzy.Info | null };
    const [idxs, info] = uf.search(haystack.value, needle);
    return { idxs: idxs ?? [], info };
  });

  const indexesLimited = computed(() => {
    const { idxs } = searchData.value;
    return limit !== undefined ? idxs.slice(0, limit) : idxs;
  });

  return computed(() => {
    const { info } = searchData.value;
    const srcItems = toValue(items);
    const results: FuzzyResult<T>[] = [];

    indexesLimited.value.forEach((hIdx, pos) => {
      const src = srcItems[hIdx];
      const key = getter(src);
      const nameEnd = !key.includes(ID_SEP) ? key.length : key.indexOf(ID_SEP);
      const allRanges = info?.ranges?.[pos] ?? [];
      const nameRanges: number[] = [];
      for (let i = 0; i < allRanges.length; i += 2) {
        const s = allRanges[i];
        const e = allRanges[i + 1];
        if (e <= nameEnd) nameRanges.push(s, e);
      }
      const namePart = key.slice(0, nameEnd);
      const highlighted = nameRanges.length > 0 ? UFuzzy.highlight(namePart, nameRanges, mark, "") : namePart;
      const highlight = hideIdHighlight ? highlighted : `${highlighted}${key.slice(nameEnd)}`;
      results.push({ ...src, highlight });
    });

    return results;
  });
}
